import React from 'react';
import { Box, Typography, FormControlLabel, Switch, TextField } from '@mui/material';
import { adminFieldSx } from '../../../pages/admin/adminUi';
import { isArgsOutputMode, resolveAssertOn, structTypeHint } from '../../../common/functionModeTypes';
import {
  parseFieldValue,
  formatFieldValue,
  applyParsedField,
  fieldHelperForType,
} from '../../../common/functionMode';
import TypedTestCaseField from '../../TypedTestCaseField';

export { parseFieldValue, formatFieldValue };

/**
 * Simple typed fields for function-mode test cases (no raw JSON unless advanced).
 */
const FunctionTestCaseEditor = ({
  signature,
  testCase,
  onChange,
  showExplanation = true,
  caseKey = '',
}) => {
  const params = signature?.params || [];
  const returnType = signature?.returnType || 'int';
  const argsMode = isArgsOutputMode(signature);
  const assertOn = resolveAssertOn(signature);
  const assertParams = assertOn
    .map((name) => params.find((p) => p.name === name))
    .filter(Boolean);

  const fieldLabel = (name, type, kind) => {
    const hint = structTypeHint(type);
    if (hint) return `${name} (${type})`;
    return name;
  };

  const fieldHelper = (type, kind) => {
    const structHint = structTypeHint(type);
    if (structHint) return `${kind} · ${structHint}`;
    return `${kind} · ${type}${fieldHelperForType(type) ? ` · ${fieldHelperForType(type)}` : ''}`;
  };

  const updateArgs = (name, type, parsed) => {
    onChange((prev) => ({
      ...prev,
      args: applyParsedField(prev?.args, name, type, parsed),
    }));
  };

  const updateExpectedReturn = (parsed) => {
    onChange((prev) => ({
      ...prev,
      expected: parsed,
    }));
  };

  const updateExpectedArg = (name, type, parsed) => {
    onChange((prev) => {
      const prevExpected =
        prev?.expected &&
        typeof prev.expected === 'object' &&
        !Array.isArray(prev.expected)
          ? prev.expected
          : {};
      return {
        ...prev,
        expected: applyParsedField(prevExpected, name, type, parsed),
      };
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {params.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Add parameters in the method signature above.
        </Typography>
      )}
      {params.map((param) => (
        <TypedTestCaseField
          key={param.name}
          fieldKey={`${caseKey}-${param.name}-arg`}
          type={param.type}
          label={fieldLabel(param.name, param.type, 'input')}
          helperText={fieldHelper(param.type, 'Input')}
          value={testCase.args?.[param.name]}
          onChange={(parsed) => updateArgs(param.name, param.type, parsed)}
          sx={adminFieldSx}
        />
      ))}

      {argsMode ? (
        <>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Expected state after the call (mutated parameters)
          </Typography>
          {assertParams.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Add parameters or choose which ones to assert in the signature section.
            </Typography>
          )}
          {assertParams.map((param) => (
            <TypedTestCaseField
              key={`expected-${param.name}`}
              fieldKey={`${caseKey}-${param.name}-expected`}
              type={param.type}
              label={fieldLabel(param.name, param.type, 'expected')}
              helperText={fieldHelper(param.type, 'Expected')}
              value={
                testCase.expected && typeof testCase.expected === 'object'
                  ? testCase.expected[param.name]
                  : ''
              }
              onChange={(parsed) => updateExpectedArg(param.name, param.type, parsed)}
              sx={adminFieldSx}
            />
          ))}
        </>
      ) : (
        <TypedTestCaseField
          fieldKey={`${caseKey}-expected-return`}
          type={returnType}
          label={fieldLabel('expected', returnType, 'expected')}
          helperText={fieldHelper(returnType, 'Expected')}
          value={testCase.expected}
          onChange={updateExpectedReturn}
          sx={adminFieldSx}
        />
      )}

      {showExplanation && (
        <TextField
          label="Explanation (shown to students)"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={testCase.explanation || ''}
          onChange={(e) => onChange({ ...testCase, explanation: e.target.value })}
          sx={adminFieldSx}
        />
      )}

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={Boolean(testCase.isSample)}
            onChange={(e) => onChange({ ...testCase, isSample: e.target.checked })}
          />
        }
        label="Show as sample example on problem page"
      />
    </Box>
  );
};

export default FunctionTestCaseEditor;
